import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { Account } from '../../../accounting/model/account';
import { AccountingAccountService } from '../../../accounting/service/data/accounting-account.service';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';
import { MessageService } from '../../../core/service/message.service';
import { CompanyBankAccountService } from '../../../invoice/service/data/company-bank-account.service';
import { Bank } from '../../model/bank';
import {
  CompanyBankAccount,
  CompanyBankAccountCreate,
  CompanyBankAccountInput,
  CompanyBankAccountUpdate,
} from '../../model/company-bank-account';
import { Company } from '../../model/company';

@Component({
  selector: 'app-company-bank-account',
  templateUrl: './company-bank-account.component.html',
  providers: [AccountingAccountService, CompanyBankAccountService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyBankAccountComponent implements OnInit, OnDestroy {
  @Input() banks: Bank[] = [];
  @Input() accountPlanId: string;
  @Input() companyBankAccount: CompanyBankAccount;
  @Input() company: Company;

  @Output() saved = new EventEmitter<CompanyBankAccount>();
  @Output() deleted = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  readonly clearingMask = /^\d{0,4}/;
  readonly bankAccountMask = /^\d{0,10}/;
  readonly bankGiroMask = /^(\d{0,4}-?\d{0,4})/;
  readonly dimensionMask = /^[^@]*$/;

  formGroup: FormGroup;
  bankName = '';
  saving = false;

  private readonly subscription = new Subscription();

  constructor(
    private readonly formControlFactory: FormControlExFactory,
    private readonly bankAccountService: CompanyBankAccountService,
    private readonly languageService: LanguageService,
    private readonly messageService: MessageService,
    readonly accountFilterService: AccountingAccountService
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.updateBankName(this.formGroup.controls['clearingNumber'].value);
    this.subscription.add(this.formGroup.controls['clearingNumber'].valueChanges.subscribe(value => this.updateBankName(value)));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get isNew(): boolean {
    return !this.companyBankAccount?.id;
  }

  get canDelete(): boolean {
    return !this.isNew && !this.companyBankAccount.defaultInvoiceAccount;
  }

  onAccountSelected(item: AutoCompleteSelectionItem<Account>): void {
    this.formGroup.controls['accountId'].setValue(item?.data?.id ?? null);
  }

  save(): void {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid || this.saving) return;

    this.saving = true;
    const input: CompanyBankAccountInput = Object.assign({}, this.formGroup.getRawValue(), {
      iban: this.normalizeIban(this.formGroup.controls['iban'].value),
      bic: this.normalizeBic(this.formGroup.controls['bic'].value),
    });
    const request = this.isNew
      ? this.bankAccountService.createNew(Object.assign({ companyId: this.company.id }, input) as CompanyBankAccountCreate)
      : this.bankAccountService.updateExisting(Object.assign({ id: this.companyBankAccount.id }, input) as CompanyBankAccountUpdate);

    this.subscription.add(
      request.pipe(finalize(() => (this.saving = false))).subscribe({
        next: account => this.saved.emit(account),
        error: error => this.messageService.showError(error),
      })
    );
  }

  deleteAccount(): void {
    if (!this.canDelete || this.saving) return;

    const message = this.languageService.getText('COMPANY.BANK_ACCOUNT.DELETE_CONFIRM', { name: this.companyBankAccount.name });
    if (!window.confirm(message)) return;

    this.saving = true;
    this.subscription.add(
      this.bankAccountService
        .delete(this.companyBankAccount.id)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe(() => this.deleted.emit())
    );
  }

  cancel(): void {
    if (!this.saving) this.cancelled.emit();
  }

  formatInternationalFields(): void {
    this.formGroup.controls['iban'].setValue(this.formatIban(this.formGroup.controls['iban'].value));
    this.formGroup.controls['bic'].setValue(this.normalizeBic(this.formGroup.controls['bic'].value));
  }

  private createForm(): void {
    const model = this.companyBankAccount;
    this.formGroup = new FormGroup({
      name: this.requiredControl('COMPANY.BANK_ACCOUNT.NAME', model.name, [this.maxLengthValidator(50)]),
      clearingNumber: this.requiredControl('COMPANY.BANK_ACCOUNT.CLEARING_NO', model.clearingNumber, [
        this.patternValidator(/^\d{4}$/, 'COMPANY.BANK_ACCOUNT.ERROR.CLEARING'),
      ]),
      accountNumber: this.requiredControl('COMPANY.BANK_ACCOUNT.ACCOUNT_NO', model.accountNumber, [
        this.patternValidator(/^\d{1,10}$/, 'COMPANY.BANK_ACCOUNT.ERROR.ACCOUNT_NUMBER'),
      ]),
      accountId: this.requiredControl('COMPANY.BANK_ACCOUNT.ACCOUNTING_ACCOUNT', model.accountId),
      bankGiroNumber: this.formControlFactory.create(
        'COMPANY.BANK_ACCOUNT.BANKGIRO_NUMBER',
        model.bankGiroNumber,
        this.bankGiroValidator
      ),
      iban: this.formControlFactory.create('COMPANY.BANK_ACCOUNT.IBAN', model.iban, this.ibanValidator),
      bic: this.formControlFactory.create('COMPANY.BANK_ACCOUNT.BIC', model.bic, this.bicValidator),
      customerNumber: this.formControlFactory.create(
        'COMPANY.BANK_ACCOUNT.CUSTOMER_NUMBER',
        model.customerNumber,
        null,
        null,
        'COMPANY.BANK_ACCOUNT.HELP_TEXT.CUSTOMER_NUMBER'
      ),
      costPlace: this.formControlFactory.create(
        'COMPANY.BANK_ACCOUNT.COST_PLACE',
        model.costPlace,
        this.dimensionValidator,
        null,
        'COMPANY.BANK_ACCOUNT.HELP_TEXT.CODEPART_USAGE'
      ),
      project: this.formControlFactory.create(
        'COMPANY.BANK_ACCOUNT.PROJECT',
        model.project,
        this.dimensionValidator,
        null,
        'COMPANY.BANK_ACCOUNT.HELP_TEXT.CODEPART_USAGE'
      ),
      costBearer: this.formControlFactory.create(
        'COMPANY.BANK_ACCOUNT.COST_BEARER',
        model.costBearer,
        this.dimensionValidator,
        null,
        'COMPANY.BANK_ACCOUNT.HELP_TEXT.CODEPART_USAGE'
      ),
      counterpart: this.formControlFactory.create(
        'COMPANY.BANK_ACCOUNT.COUNTERPART',
        model.counterpart,
        this.dimensionValidator,
        null,
        'COMPANY.BANK_ACCOUNT.HELP_TEXT.CODEPART_USAGE'
      ),
      bankTransactionsImportStartDate: this.formControlFactory.create(
        'BANK_IMPORT_TRANSACTIONS.ADD_STARTDATE_BANK_IMPORT',
        model.bankTransactionsImportStartDate
      ),
    });
  }

  private requiredControl(caption: string, value: unknown, validators: ValidatorFn[] = []): FormControlEx {
    const control = this.formControlFactory.create(caption, value, validators);
    control.required = true;
    return control;
  }

  private updateBankName(clearingText: string): void {
    const clearingNumber = Number(clearingText);
    this.bankName =
      clearingText?.length === 4 && Number.isFinite(clearingNumber)
        ? this.banks.find(bank => clearingNumber >= bank.clearingNumberFrom && clearingNumber <= bank.clearingNumberTo)?.name ?? ''
        : '';
  }

  private readonly ibanValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = this.normalizeIban(control.value);
    if (!value) return null;
    if (!/^[A-Z]{2}[A-Z0-9]{13,32}$/.test(value)) return { iban: { message: 'COMPANY.BANK_ACCOUNT.ERROR.IBAN' } };

    const rearranged = value.slice(4) + value.slice(0, 4);
    let remainder = 0;
    for (const character of rearranged) {
      const digits = /[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character;
      for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1 ? null : { iban: { message: 'COMPANY.BANK_ACCOUNT.ERROR.IBAN' } };
  };

  private readonly bankGiroValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const value = String(control.value).replace(/[ -]/g, '').replace(/^0+/, '');
    return /^\d{7,8}$/.test(value) ? null : { bankGiro: { message: 'COMPANY.BANK_ACCOUNT.ERROR.BANKGIRO' } };
  };

  private readonly bicValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    return /^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(this.normalizeBic(control.value))
      ? null
      : { bic: { message: 'COMPANY.BANK_ACCOUNT.ERROR.BIC' } };
  };

  private readonly dimensionValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    control.value && String(control.value).includes('@') ? { dimension: { message: 'COMPANY.BANK_ACCOUNT.ERROR.DIMENSION' } } : null;

  private patternValidator(pattern: RegExp, message: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      !control.value || pattern.test(control.value) ? null : { bankAccountPattern: { message } };
  }

  private maxLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      control.value && String(control.value).length > maxLength
        ? { maxlength: { requiredLength: maxLength, actualLength: String(control.value).length } }
        : null;
  }

  private normalizeIban(value: unknown): string {
    return String(value ?? '')
      .replace(/[ -]/g, '')
      .toUpperCase();
  }

  private formatIban(value: unknown): string {
    return this.normalizeIban(value).replace(/(.{4})(?=.)/g, '$1 ');
  }

  private normalizeBic(value: unknown): string {
    return String(value ?? '')
      .replace(/\s/g, '')
      .toUpperCase();
  }
}
